import { useState } from 'react'
import { Alert, DatePicker, Form, Input, InputNumber, Modal, Segmented, Select, Switch, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePlanOptions, useVispOptions } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import { displayName } from '@/shared/lib/planName'
import { tenantToday } from '@/shared/lib/dayRange'
import { endOfDayIso, formatNumber } from '@/shared/lib/format'
import { searchAgents } from '@/features/agent/agentApi'
import {
  CREDENTIAL_MODES,
  generateVoucherBatch,
  LOGIN_DIGITS,
  loginBatchLimit,
  PASSWORD_DIGITS,
  VOUCHER_INSTRUCTIONS_MAX,
  VOUCHER_NOTE_MAX,
  type CredentialMode,
  type VoucherBatchDraft,
} from './voucherApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface VoucherBatchFormModalProps {
  open: boolean
  onClose: () => void
}

interface FormValues {
  planId: string
  agentId?: string
  credentialMode: CredentialMode
  codePrefix?: string
  codeLength: number
  loginDigits: number
  passwordDigits: number
  printQr: boolean
  quantity: number
  validUntil?: Dayjs | null
  note?: string
  vispId?: string
  instructions?: string
}

const CODE_LENGTH_MIN = 6
const CODE_LENGTH_MAX = 32
const QUANTITY_MAX = 100_000
/** Backenddagi `VoucherBatchRequest.codePrefix` naqshi (`subscriber.api`) — aynan o'zi */
const PREFIX_PATTERN = /^$|^[A-Z0-9-]{1,12}$/

/**
 * Karta partiyasi generatsiyasi.
 *
 * `agentId` va `planId` ikkalasi ham TANLOV — free-text UUID kiritish yo'q,
 * chunki noto'g'ri id backend tomonidan FK xatosi bilan rad etilardi va
 * operator sababini tushunmasdi.
 *
 * ⚠️ XATO OYNANING ICHIDA qoladi (`VoucherVoidModal` naqshi): sahifa
 * banneriga yozilgan xabar modal niqobi ortida turardi va operator faqat
 * spinner to'xtaganini ko'rardi.
 */
export function VoucherBatchFormModal({ open, onClose }: VoucherBatchFormModalProps) {
  const { t, i18n } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const [agentSearch, setAgentSearch] = useState('')

  // ⚠️ `state === 'ACTIVE'` EMAS: siyosati unutilgan tarif ham ACTIVE
  // bo'ladi, lekin u bilan abonent ulana olmaydi. Bunday tarifga karta
  // bosib chiqarish — bir partiya o'lik karta degani.
  const plans = usePlanOptions(open).filter((p) => p.sellable)

  const { canAll } = usePermissions()
  const canPickVisp = canAll('subscriber.visp.assign', 'network.visp.read')
  const visps = useVispOptions(open && canPickVisp).filter((v) => v.assignable && !v.pooled)

  const credentials = Form.useWatch('credentialMode', form) === 'LOGIN_PASSWORD'
  const loginDigits = Form.useWatch('loginDigits', form)
  const loginLimit = credentials && loginDigits ? Math.min(QUANTITY_MAX, loginBatchLimit(loginDigits)) : null

  const agents = useQuery({
    queryKey: ['agents', 'voucherSearch', agentSearch],
    queryFn: () => searchAgents(agentSearch),
    enabled: open && agentSearch.trim().length >= 2,
  })

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const draft: VoucherBatchDraft = {
        planId: values.planId,
        agentId: values.agentId ?? null,
        codePrefix: values.codePrefix?.trim().toUpperCase() || null,
        codeLength: values.credentialMode === 'LOGIN_PASSWORD' ? values.loginDigits : values.codeLength,
        quantity: values.quantity,
        // ⚠️ Kun OXIRI tenant zonasida (`dayRange.ts`). Ilgari
        // `endOf('day')` brauzernikini olardi: Kobul tenantida karta
        // amal muddati yarim soat erta tugardi.
        validUntil: values.validUntil
          ? endOfDayIso(values.validUntil.format('YYYY-MM-DD'))
          : null,
        note: values.note?.trim() || null,
        vispId: canPickVisp ? (values.vispId ?? null) : null,
        instructions: values.instructions?.trim() || null,
        credentialMode: values.credentialMode,
        loginDigits: values.loginDigits,
        passwordDigits: values.passwordDigits,
        printQr: values.printQr,
      }
      return generateVoucherBatch(draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.batches })
      form.resetFields()
      onClose()
    },
  })

  const handleClose = () => {
    form.resetFields()
    setAgentSearch('')
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('voucher.batch.createTitle')}
      onCancel={handleClose}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('operation.create')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <Typography.Paragraph type="secondary">{t('voucher.batch.createHint')}</Typography.Paragraph>

      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues>
        form={form}
        layout="vertical"
        preserve={false}
        disabled={save.isPending}
        initialValues={{
          credentialMode: 'CODE',
          codeLength: 10,
          loginDigits: 8,
          passwordDigits: 6,
          printQr: true,
          quantity: 100,
        }}
      >
        <Form.Item
          name="planId"
          label={t('voucher.batch.plan')}
          rules={[{ required: true, message: t('voucher.batch.planRequired') }]}
        >
          <Select
            autoFocus
            showSearch
            optionFilterProp="label"
            options={plans.map((p) => ({ value: p.id, label: displayName(p, i18n.language) }))}
          />
        </Form.Item>

        {/*
          ⚠️ IKKI JUMLA, IKKI SAVOL — va ikkinchisi AYNAN chalkashlik
          manbai edi. Egasi «agent tanlash nimaga kerak» deb so'radi,
          chunki ekranda hech qayerda yozilmagan ediki:
            · maydon NARXNI hal qiladi (partiyada qotib qoladi),
              daromad va zaxira ham shunga yoziladi;
            · lekin u AKTIVATSIYAGA umuman ta'sir qilmaydi —
              `VoucherServiceImpl.activate` partiya egasini
              tekshirmaydi va kartani istalgan agent yoki reseller
              faollashtira oladi.
          Ikkinchisi ALOHIDA qatorda: birinchisining oxiriga qo'shilsa
          u ko'zdan qochardi, holbuki aynan u kutilmagan qoida.
        */}
        <Form.Item
          name="agentId"
          label={t('voucher.batch.agent')}
          extra={
            <>
              {t('voucher.batch.agentHint')}
              <br />
              {t('voucher.batch.agentActivation')}
            </>
          }
        >
          <Select
            allowClear
            showSearch
            filterOption={false}
            notFoundContent={
              agentSearch.trim().length < 2 ? t('voucher.batch.agentSearchHint') : t('common.empty')
            }
            loading={agents.isFetching}
            onSearch={setAgentSearch}
            options={(agents.data?.nodes ?? []).map((a) => ({
              value: a.id,
              label: `${a.displayName} (${a.code})`,
            }))}
          />
        </Form.Item>

        {canPickVisp ? (
          <Form.Item name="vispId" label={t('voucher.batch.visp')} extra={t('voucher.batch.vispHint')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={visps.map((v) => ({ value: v.id, label: `${v.centerName} / ${v.name}` }))}
            />
          </Form.Item>
        ) : null}

        <Form.Item name="note" label={t('voucher.batch.note')} extra={t('voucher.batch.noteHint')}>
          <Input maxLength={VOUCHER_NOTE_MAX} showCount />
        </Form.Item>

        <Form.Item
          name="instructions"
          label={t('voucher.batch.instructions')}
          extra={t('voucher.batch.instructionsHint')}
        >
          <Input.TextArea rows={2} maxLength={VOUCHER_INSTRUCTIONS_MAX} showCount />
        </Form.Item>

        <Form.Item
          name="credentialMode"
          label={t('voucher.batch.credentialMode')}
          extra={t(credentials ? 'voucher.batch.credentialModeLoginHint' : 'voucher.batch.credentialModeCodeHint')}
        >
          <Segmented<CredentialMode>
            block
            options={CREDENTIAL_MODES.map((mode) => ({ value: mode, label: t(`voucher.credentialMode.${mode}`) }))}
          />
        </Form.Item>

        {credentials ? (
          <>
            <Form.Item
              name="loginDigits"
              label={t('voucher.batch.loginDigits')}
              extra={t('voucher.batch.loginDigitsHint', LOGIN_DIGITS)}
              rules={[{ required: true, message: t('voucher.batch.loginDigitsRequired') }]}
            >
              <InputNumber min={LOGIN_DIGITS.min} max={LOGIN_DIGITS.max} precision={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="passwordDigits"
              label={t('voucher.batch.passwordDigits')}
              extra={t('voucher.batch.passwordDigitsHint', PASSWORD_DIGITS)}
              rules={[{ required: true, message: t('voucher.batch.passwordDigitsRequired') }]}
            >
              <InputNumber
                min={PASSWORD_DIGITS.min}
                max={PASSWORD_DIGITS.max}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              name="codePrefix"
              label={t('voucher.batch.codePrefix')}
              extra={t('voucher.batch.codePrefixHint')}
              rules={[{ pattern: PREFIX_PATTERN, message: t('voucher.batch.codePrefixFormat') }]}
            >
              <Input placeholder="HOTEL" style={{ textTransform: 'uppercase' }} />
            </Form.Item>

            <Form.Item
              name="codeLength"
              label={t('voucher.batch.codeLength')}
              extra={t('voucher.batch.codeLengthHint', { min: CODE_LENGTH_MIN, max: CODE_LENGTH_MAX })}
              rules={[{ required: true }]}
            >
              <InputNumber min={CODE_LENGTH_MIN} max={CODE_LENGTH_MAX} style={{ width: '100%' }} />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="printQr"
          label={t('voucher.batch.printQr')}
          extra={t('voucher.batch.printQrHint')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="quantity"
          label={t('voucher.batch.quantity')}
          dependencies={['credentialMode', 'loginDigits']}
          extra={
            loginLimit === null
              ? t('voucher.batch.quantityHint', { max: QUANTITY_MAX })
              : t('voucher.batch.quantityLoginHint', { max: formatNumber(loginLimit), digits: loginDigits })
          }
          rules={[
            { required: true, type: 'number', min: 1, max: QUANTITY_MAX },
            ...(loginLimit === null
              ? []
              : [
                  {
                    type: 'number' as const,
                    max: loginLimit,
                    message: t('voucher.batch.quantityLoginLimit', { max: formatNumber(loginLimit) }),
                  },
                ]),
          ]}
        >
          <InputNumber min={1} max={QUANTITY_MAX} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="validUntil"
          label={t('voucher.batch.validUntil')}
          extra={t('voucher.batch.validUntilHint')}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            // ⚠️ "Bugun" TENANT kalendarida (`dayRange.ts`)
            disabledDate={(d) => d.isBefore(tenantToday(), 'day')}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
